import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";

const parseMarkdownToHtml = (text) => {
  if (!text) return "";

  // 1. Escape HTML
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // 2. Headings
  html = html.replace(/^### (.*?)$/gm, "<h3>$1</h3>");
  html = html.replace(/^## (.*?)$/gm, "<h2>$1</h2>");
  html = html.replace(/^# (.*?)$/gm, "<h1>$1</h1>");

  // 3. Bold
  html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // 4. List items
  html = html.replace(/^\* (.*?)$/gm, "<li>$1</li>");

  // 5. Group lists
  html = html.replace(/((?:<li>.*?<\/li>\s*)+)/gs, "<ul>$1</ul>");

  // 6. Markdown links
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #007bff; text-decoration: underline;">$1</a>');

  // 7. Make bold to Microvision Infotech
  html = html.replace(/Microvision Infotech/g, "<strong>Microvision Infotech</strong>");

  // 8. Make bold to website: https://microvisioninfotech.com, WhatsApp: +91 831-920-3408, or email: contact@microvisioninfotech.com
  const signatureRegex = /(?:our\s+)?website:\s*https?:\/\/microvisioninfotech\.com\/?,\s*WhatsApp:\s*\+?91\s*831-?920-?3408,\s*or\s*email:\s*[\w.-]+@microvisioninfotech\.com/gi;
  html = html.replace(signatureRegex, "<strong>$&</strong>");

  // 9. Format links & colors for URLs: https://microvisioninfotech.com/ or https://microvisioninfotech.com
  const urlRegex = /https?:\/\/microvisioninfotech\.com\/?/gi;
  html = html.replace(urlRegex, '<a href="https://microvisioninfotech.com/" target="_blank" rel="noopener noreferrer" style="color: #c7885e; text-decoration: underline; font-weight: inherit;">https://microvisioninfotech.com/</a>');

  // 10. Format link & color for email: contact@microvisioninfotech.com
  const emailRegex = /contact@microvisioninfotech\.com/gi;
  html = html.replace(emailRegex, '<a href="mailto:contact@microvisioninfotech.com" style="color: #c7885e; text-decoration: underline; font-weight: inherit;">contact@microvisioninfotech.com</a>');

  // 11. Format color for phone: +91 831-920-3408
  const phoneTagAwareRegex = /(<[^>]+>)|(\+?91\s*831-?920-?3408)/gi;
  html = html.replace(phoneTagAwareRegex, (match, tag, number) => {
    if (tag) return tag;
    return `<span style="color: #c7885e;">${number}</span>`;
  });

  // 12. Format color for Mexa / मेक्सा
  const mexaTagAwareRegex = /(<[^>]+>)|(Mexa|मेक्सा)/gi;
  html = html.replace(mexaTagAwareRegex, (match, tag, word) => {
    if (tag) return tag;
    return `<span style="color: #c7885e;">${word}</span>`;
  });

  // 13. Paragraph split
  const lines = html.split("\n");
  const processedLines = lines.map((line) => {
    const trimmed = line.trim();
    if (!trimmed) return "";
    if (
      trimmed.startsWith("<h") ||
      trimmed.startsWith("<ul") ||
      trimmed.startsWith("<li") ||
      trimmed.endsWith("</ul>") ||
      trimmed.endsWith("</li>")
    ) {
      return line;
    }
    return `<p style="margin-bottom: 8px;">${line}</p>`;
  });

  return processedLines.join("");
};

const Typewriter = ({ text, speed = 15, onType, onComplete }) => {
  const [displayedText, setDisplayedText] = useState("");
  const onTypeRef = useRef(onType);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onTypeRef.current = onType;
    onCompleteRef.current = onComplete;
  }, [onType, onComplete]);

  useEffect(() => {
    let index = 0;
    let currentText = "";
    
    // Determine dynamically how many characters to add at a time for longer responses
    const step = text.length > 600 ? 5 : text.length > 300 ? 3 : text.length > 100 ? 2 : 1;

    const interval = setInterval(() => {
      if (index < text.length) {
        currentText += text.slice(index, index + step);
        index += step;
        setDisplayedText(currentText);
        if (onTypeRef.current) onTypeRef.current();
      } else {
        clearInterval(interval);
        if (onCompleteRef.current) onCompleteRef.current();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  return (
    <div 
      className="chat-text" 
      dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(displayedText) }} 
    />
  );
};

const Intro = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [popupQuery, setPopupQuery] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const chatEndRef = useRef(null);

  const sendMessageToAI = async (questionText) => {
    if (!questionText.trim()) return;

    const userMsg = { type: "user", text: questionText.trim() };
    const loadingBotMsg = { type: "bot", text: "", loading: true };

    setQuery("");
    setPopupQuery("");
    setChatHistory((prev) => [...prev, userMsg, loadingBotMsg]);
    setIsModalOpen(true);

    try {
      const response = await fetch("/api/ai-answer", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userQuestion: questionText.trim(),
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setChatHistory((prev) => {
          const newHistory = [...prev];
          const idx = newHistory.map((m) => m.loading).lastIndexOf(true);
          if (idx !== -1) {
            newHistory[idx] = { type: "bot", text: data.answer, loading: false, animate: true };
          }
          return newHistory;
        });
      } else {
        setChatHistory((prev) => {
          const newHistory = [...prev];
          const idx = newHistory.map((m) => m.loading).lastIndexOf(true);
          if (idx !== -1) {
            newHistory[idx] = {
              type: "bot",
              text: "",
              error: data.error || "An error occurred.",
              loading: false,
            };
          }
          return newHistory;
        });
      }
    } catch (err) {
      console.error(err);
      setChatHistory((prev) => {
        const newHistory = [...prev];
        const idx = newHistory.map((m) => m.loading).lastIndexOf(true);
        if (idx !== -1) {
          newHistory[idx] = {
            type: "bot",
            text: "",
            error: "Failed to connect to the server.",
            loading: false,
          };
        }
        return newHistory;
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessageToAI(query);
    }
  };

  const handleCopyMessage = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]);

  const [placeholderText, setPlaceholderText] = useState("");

  useEffect(() => {
    const s1 = t('intro.typewriterSentence1', 'How can you help me grow my business?');
    const s2 = t('intro.typewriterSentence2', 'Can you build a mobile app and a website for my business?');
    const sentences = [s1, s2];
    
    let currentSentenceIndex = 0;
    let currentCharIndex = 0;
    let isDeleting = false;
    let typingSpeed = 70;
    let deletingSpeed = 40;
    let timer;
    
    const handleType = () => {
      const fullText = sentences[currentSentenceIndex];
      
      if (!isDeleting) {
        setPlaceholderText(fullText.substring(0, currentCharIndex + 1));
        currentCharIndex++;
        
        if (currentCharIndex === fullText.length) {
          isDeleting = true;
          timer = setTimeout(handleType, 2000);
          return;
        }
        timer = setTimeout(handleType, typingSpeed);
      } else {
        setPlaceholderText(fullText.substring(0, currentCharIndex - 1));
        currentCharIndex--;
        
        if (currentCharIndex === 0) {
          isDeleting = false;
          currentSentenceIndex = (currentSentenceIndex + 1) % sentences.length;
          timer = setTimeout(handleType, 500);
          return;
        }
        timer = setTimeout(handleType, deletingSpeed);
      }
    };
    
    timer = setTimeout(handleType, 500);
    
    return () => clearTimeout(timer);
  }, [t]);

  useEffect(() => {
    const initParticles = () => {
      if (window.particlesJS) {
        window.particlesJS("particles-js", {
          particles: {
            number: { value: 70, density: { enable: true, value_area: 800 } },
            color: { value: "#ffffff" },
            shape: {
              type: "circle",
              stroke: { width: 0, color: "#000000" },
              polygon: { nb_sides: 5 }
            },
            opacity: {
              value: 0.4,
              random: false,
              anim: { enable: false, speed: 1, opacity_min: 0.1, sync: false }
            },
            size: {
              value: 3,
              random: true,
              anim: { enable: false, speed: 40, size_min: 0.1, sync: false }
            },
            line_linked: {
              enable: true,
              distance: 140,
              color: "#ffffff",
              opacity: 0.35,
              width: 1
            },
            move: {
              enable: true,
              speed: 2.5,
              direction: "none",
              random: false,
              straight: false,
              out_mode: "out",
              bounce: false,
              attract: { enable: false, rotateX: 600, rotateY: 1200 }
            }
          },
          interactivity: {
            detect_on: "canvas",
            events: {
              onhover: { enable: true, mode: "grab" },
              onclick: { enable: true, mode: "push" },
              resize: true
            },
            modes: {
              grab: { distance: 160, line_linked: { opacity: 0.8 } },
              bubble: { distance: 400, size: 40, duration: 2, opacity: 8, speed: 3 },
              repulse: { distance: 200, duration: 0.4 },
              push: { particles_nb: 4 },
              remove: { particles_nb: 2 }
            }
          },
          retina_detect: true
        });
      }
    };

    // Try loading particles, retrying if the library is not immediately parsed
    let retryCount = 0;
    const interval = setInterval(() => {
      if (window.particlesJS) {
        initParticles();
        clearInterval(interval);
      } else {
        retryCount++;
        if (retryCount > 20) {
          clearInterval(interval);
        }
      }
    }, 200);

    return () => clearInterval(interval);
  }, []);

  const renderHighlightedText = (text) => {
    if (!text) return null;
    const regex = /(Mexa|मेक्सा)/gi;
    const parts = text.split(regex);
    return parts.map((part, index) => {
      if (part && (part.toLowerCase() === "mexa" || part === "मेक्सा")) {
        return <span key={index} style={{ color: "#c7885e" }}>{part}</span>;
      }
      return part;
    });
  };

  const handleTypewriterComplete = (index) => {
    setChatHistory((prev) => {
      const newHistory = [...prev];
      if (newHistory[index]) {
        newHistory[index] = { ...newHistory[index], animate: false };
      }
      return newHistory;
    });
  };

  const scrollToBottom = () => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  };

  const handleScrollDown = (e) => {
    e.preventDefault();
    const target = document.querySelector("#products");
    if (target) {
      let top_space = 0;
      const header = document.getElementById("header");
      if (header) {
        top_space = header.offsetHeight;
        if (!header.classList.contains("header-scrolled")) {
          top_space -= 20;
        }
      }
      window.scroll({
        top: target.offsetTop - top_space,
        behavior: "smooth",
      });
    }
  };

  return (
    <>
      <style>{`
        @keyframes blink {
          50% { opacity: 0; }
        }
        .chat-bubble.typing-active p:last-child::after {
          content: '▊';
          display: inline-block;
          margin-left: 4px;
          color: #c7885e;
          animation: blink 1s step-end infinite;
        }
        .hero-price-tagline {
          font-size: 26px;
          font-weight: 700;
          color: #C7885E;
          margin-top: 30px;
          margin-bottom: 5px;
          text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
          letter-spacing: 0.5px;
        }
        @media (max-width: 768px) {
          .hero-price-tagline {
            font-size: 19px;
            margin-top: 20px;
          }
        }
        .scroll-down-indicator {
          margin-top: 10px;
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          cursor: pointer;
          opacity: 0.85;
          transition: all 0.3s ease;
          text-decoration: none !important;
        }
        .scroll-down-indicator:hover {
          opacity: 1;
          color: #74b5fc !important;
          transform: translateY(2px);
        }
        .scroll-down-indicator i {
          font-size: 20px;
          animation: bounceDown 1.8s infinite;
        }
        @keyframes bounceDown {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(8px);
          }
          60% {
            transform: translateY(4px);
          }
        }
        .sub-header-banner {
          position: fixed;
          left: 0;
          right: 0;
          width: 100%;
          background-color: #002244;
          color: #ffffff;
          text-align: center;
          padding: 12px 15px;
          font-size: 16px;
          font-weight: 500;
          top: 100px;
          z-index: 996;
          box-shadow: 0 4px 10px rgba(0,0,0,0.15);
          transition: top 0.5s, background-color 0.5s;
          border-bottom: 1px solid rgba(255,255,255,0.1);
        }
        @media (max-width: 991px) {
          .sub-header-banner {
            top: 60px;
            font-size: 14px;
            padding: 10px;
          }
        }
        #intro {
          position: relative;
          overflow: hidden;
          padding: 180px 0 100px 0;
          min-height: 100vh;
          display: flex;
          align-items: center;
        }
        @media (max-width: 991px) {
          #intro {
            padding: 120px 0 80px 0;
            min-height: 100vh;
          }
        }
        #particles-js {
          position: absolute;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
          z-index: 1;
          pointer-events: none;
        }
        #intro .container {
          position: relative;
          z-index: 2;
        }
        .search-container {
          max-width: 720px;
          margin: 0 auto;
        }
        .search-slogan-text {
          color: #ffffff;
          font-size: 22px;
          font-weight: 500;
          line-height: 1.6;
          margin-bottom: 25px;
          max-width: 760px;
          margin-left: auto;
          margin-right: auto;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        @media (max-width: 767px) {
          .search-slogan-text {
            font-size: 18px;
          }
        }
        .search-slogan-text i {
          background: rgba(255, 255, 255, 0.2);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #007bff !important;
          margin-right: 8px;
          font-size: 14px;
          box-shadow: 0 4px 10px rgba(0, 123, 255, 0.2);
        }
        .search-bar-wrapper {
          background: #ffffff;
          border-radius: 30px;
          padding: 6px 8px 6px 20px;
          display: flex;
          align-items: center;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
          border: 1px solid rgba(0, 0, 0, 0.08);
          transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        .search-bar-wrapper:focus-within {
          box-shadow: 0 16px 48px rgba(0, 123, 255, 0.35);
          border-color: #007bff;
          transform: translateY(-2px);
        }
        .search-input {
          flex: 1;
          border: none;
          outline: none;
          resize: none;
          background: transparent;
          font-size: 16px;
          color: #333333;
          height: 38px;
          line-height: 1.5;
          padding: 7px 0;
          overflow-y: auto;
        }
        .search-btn {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background: #007bff;
          border: none;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }
        .search-btn:hover {
          background-color: #0056b3;
          transform: scale(1.08);
        }
        .search-btn:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        .ai-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(6px);
          z-index: 2000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 15px;
          animation: fadeIn 0.3s ease-out;
        }
        .ai-modal-container {
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-radius: 20px;
          width: 100%;
          max-width: 850px;
          height: 80vh;
          max-height: 700px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          animation: slideUp 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        @media (max-width: 991px) {
          .ai-modal-container {
            width: 95%;
            height: 85vh;
            max-height: none;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .ai-modal-header {
          padding: 16px 20px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #ffffff;
        }
        .ai-modal-title {
          font-size: 18px;
          font-weight: 700;
          color: #00366f;
        }
        .ai-modal-close {
          background: none;
          border: none;
          color: #64748b;
          font-size: 28px;
          cursor: pointer;
          line-height: 1;
          transition: color 0.2s;
          padding: 0;
          margin-left: 10px;
        }
        .ai-modal-close:hover {
          color: #00366f;
        }
        .ai-modal-body {
          flex: 1;
          padding: 20px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 15px;
          background: #ffffff;
        }
        /* Custom scrollbar for modal body */
        .ai-modal-body::-webkit-scrollbar {
          width: 6px;
        }
        .ai-modal-body::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.02);
          border-radius: 4px;
        }
        .ai-modal-body::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 4px;
        }
        .chat-bubble-row {
          display: flex;
          width: 100%;
        }
        .user-row {
          justify-content: flex-end;
        }
        .bot-row {
          justify-content: flex-start;
        }
        .chat-bubble {
          max-width: 80%;
          border-radius: 16px;
          padding: 12px 16px;
          position: relative;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
        }
        .user-row .chat-bubble {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          color: #334155;
          border-bottom-right-radius: 4px;
          text-align: left;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
        }
        .bot-row .chat-bubble {
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          color: #334155;
          border-bottom-left-radius: 4px;
          text-align: left;
        }
        .user-row .chat-bubble-sender {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          color: #64748b;
        }
        .bot-row .chat-bubble-sender {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          color: #00366f;
        }
        .chat-text {
          font-size: 14.5px;
          line-height: 1.5;
          white-space: pre-line;
        }
        .chat-loading {
          font-size: 14px;
          opacity: 0.8;
          text-align: left;
          color: #64748b;
        }
        .chat-bubble.has-actions {
          padding-bottom: 34px;
        }
        .chat-bubble-actions {
          position: absolute;
          bottom: 6px;
          right: 14px;
          display: flex;
          justify-content: flex-end;
        }
        .chat-bubble-actions button {
          font-size: 12px;
          text-decoration: none;
          color: #64748b !important;
        }
        .chat-bubble-actions button:hover {
          color: #007bff !important;
        }
        .ai-modal-footer {
          padding: 15px 20px;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
          background: #f8fafc;
        }
        .popup-search-wrapper {
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 30px;
          padding: 6px 8px 6px 20px;
          display: flex;
          align-items: center;
          transition: all 0.3s;
        }
        .popup-search-wrapper:focus-within {
          border-color: #007bff;
          box-shadow: 0 0 10px rgba(0, 123, 255, 0.15);
        }
        .popup-search-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 15px;
          color: #1e293b;
        }
        .popup-search-input::placeholder {
          color: #94a3b8;
        }
        .popup-search-btn {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: #007bff;
          border: none;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
        }
        .popup-search-btn:hover {
          background: #0056b3;
          transform: scale(1.05);
        }
        .popup-search-btn:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }
      `}</style>

      {/* Dynamic Sub-header slogan bar */}
      <div className="sub-header-banner">
        {t('intro.slogan')}
      </div>

      {/*==========================
        Intro Section (Centered Search Layout)
      ============================*/}
      <section id="intro" className="clearfix">
        <div id="particles-js"></div>
        <div className="container">
          <div className="search-container text-center">
            {/* Ask AI Slogan Text */}
            <div className="mb-4">
              <div className="search-slogan-text">
                {renderHighlightedText(t('intro.askButton'))}
              </div>
            </div>

            {/* AI Search Box */}
            <div className="search-bar-wrapper mb-2">
              <textarea
                className="search-input"
                placeholder={placeholderText}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                rows="1"
              />
              <button
                className="search-btn ml-2"
                onClick={() => sendMessageToAI(query)}
                disabled={!query.trim()}
                title={t('intro.searchButton')}
              >
                <i className="fa fa-arrow-up"></i>
              </button>
            </div>

            {/* Big Tagline Line Above Scroll Indicator */}
            <div className="text-center">
              <h2 className="hero-price-tagline">
                {t('intro.priceQualityTagline', 'Pay the Lowest Price. Get High-Quality Services.')}
              </h2>
            </div>

            {/* Scroll Down Indicator */}
            <div className="text-center">
              <a href="#products" className="scroll-down-indicator" onClick={handleScrollDown}>
                <span>{t('intro.scrollToSeeMore', 'SCROLL TO SEE MORE')}</span>
                <i className="fa fa-chevron-down"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AI Assistant Modal Popup */}
      {isModalOpen && (
        <div className="ai-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="ai-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="ai-modal-header">
              <div className="ai-modal-title">
                <i className="fa fa-android mr-2 text-primary"></i>
                {renderHighlightedText(t('intro.popupTitle'))}
              </div>
              <div className="d-flex align-items-center">
                {chatHistory.length > 0 && (
                  <button className="btn btn-sm btn-link text-danger mr-3 p-0" onClick={() => setChatHistory([])} style={{ textDecoration: "none" }}>
                    <i className="fa fa-trash mr-1"></i> {t('intro.popupClear')}
                  </button>
                )}
                <button className="ai-modal-close" onClick={() => setIsModalOpen(false)}>
                  &times;
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="ai-modal-body">
              {chatHistory.length === 0 ? (
                <div className="ai-empty-chat text-center my-auto">
                  <i className="fa fa-comments-o fa-3x mb-3 text-muted"></i>
                  <p className="text-muted">Ask any question to get started with Microvision AI Assistant.</p>
                </div>
              ) : (
                chatHistory.map((msg, index) => (
                  <div key={index} className={`chat-bubble-row ${msg.type === "user" ? "user-row" : "bot-row"}`}>
                    <div 
                      className={`chat-bubble ${msg.animate ? "typing-active" : ""} ${msg.type === "bot" && !msg.loading && !msg.error && !msg.animate ? "has-actions" : ""}`}
                      style={msg.animate ? { cursor: "pointer" } : {}}
                      title={msg.animate ? "Click to skip typing animation" : ""}
                      onClick={() => {
                        if (msg.animate) {
                          handleTypewriterComplete(index);
                        }
                      }}
                    >
                      <div className="chat-bubble-sender">
                        <i className={`fa ${msg.type === "user" ? "fa-user-circle-o" : "fa-magic"} mr-1`}></i>
                        {msg.type === "user" ? "You" : renderHighlightedText("Mexa AI")}
                      </div>
                      
                      {msg.loading ? (
                        <div className="chat-loading">
                          <span className="spinner-border spinner-border-sm mr-2" role="status"></span>
                          Thinking...
                        </div>
                      ) : msg.error ? (
                        <div className="chat-error text-danger">
                          <i className="fa fa-exclamation-triangle mr-1" style={{ verticalAlign: "top", marginTop: "4px" }}></i>
                          <div style={{ display: "inline-block", width: "calc(100% - 20px)" }} dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(msg.error) }} />
                        </div>
                      ) : msg.type === "user" ? (
                        <div className="chat-text">{msg.text}</div>
                      ) : msg.animate ? (
                        <Typewriter
                          text={msg.text}
                          speed={15}
                          onType={scrollToBottom}
                          onComplete={() => handleTypewriterComplete(index)}
                        />
                      ) : (
                        <div className="chat-text" dangerouslySetInnerHTML={{ __html: parseMarkdownToHtml(msg.text) }} />
                      )}

                      {msg.type === "bot" && !msg.loading && !msg.error && !msg.animate && (
                        <div className="chat-bubble-actions">
                          <button
                            className="btn btn-sm btn-link text-muted p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyMessage(msg.text, index);
                            }}
                          >
                            <i className={`fa ${copiedIndex === index ? "fa-check text-success" : "fa-copy"} mr-1`}></i>
                            {copiedIndex === index ? "Copied!" : "Copy"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Modal Footer */}
            <div className="ai-modal-footer">
              <div className="popup-search-wrapper">
                <input
                  type="text"
                  className="popup-search-input"
                  placeholder={t('intro.popupPlaceholder')}
                  value={popupQuery}
                  onChange={(e) => setPopupQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      sendMessageToAI(popupQuery);
                    }
                  }}
                />
                <button
                  className="popup-search-btn"
                  onClick={() => sendMessageToAI(popupQuery)}
                  disabled={!popupQuery.trim()}
                >
                  <i className="fa fa-paper-plane"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* #intro */}
    </>
  );
};

export default Intro;